import React, { useMemo, useEffect } from 'react';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  setTicketNumber,
  selectedMenuTabState,
} from '../HomePage/ContentTabs/ContentTabsSlice';
import ApiService from '../../services/ApiService';
import { sortConfigState } from '../WagonDetailsPage/WagonDetailsPageSlice';
import {
  setEditedTicket,
  setValidationErrorsMultiLang,
  setTicketStatus,
} from '../EditTicket/EditTicketSlice';
import TicketListRow from '../HomePage/TicketList/TicketListRow';
import TicketsHistoricRow from '../WagonDetailsPage/TicketsHistoricTabContent/TicketsHistoricRow';
import WorkshopTimeRow from '../WorkshopTimePage/WorkshopTimeRow';
import useStyles from './styles';
import { userInfoState } from '../HomePage/HomeSlice';
import {
  currentViewNameState,
  userPreferencesState,
} from '../HomePage/TicketList/TicketListSlice';
import { sortConfigForWorkshopTimeState } from '../WorkshopTimePage/WorkshopTimeSlice';

export default function TicketsTableBody(props) {
  const { page, rowsPerPage, rows, headCells } = props;
  const emptyRows =
    rowsPerPage - Math.min(rowsPerPage, rows.length - page * rowsPerPage);
  const user = useSelector(userInfoState);
  const selectedTicketTable = useSelector(selectedMenuTabState);
  const currentViewName = useSelector(currentViewNameState);
  const userPreferences = useSelector(userPreferencesState);
  const { sortConfig } = userPreferences.find(
    (userPreference) => userPreference.viewName === currentViewName,
  );
  const sortConfigTicketHistoric = useSelector(sortConfigState);
  const sortConfigWorkshopTime = useSelector(sortConfigForWorkshopTimeState);
  const [defaultSortConfig, setDefaultSortConfig] = React.useState([]);

  const dispatch = useDispatch();
  const classes = useStyles();
  const history = useHistory();
  const { t } = useTranslation();

  function descendingComparator(var1, var2) {
    if (var2 < var1) {
      return -1;
    }
    if (var2 > var1) {
      return 1;
    }
    return 0;
  }

  function transformStringToDate(date) {
    if (date !== '-') {
      date = date.split('/');
      return new Date(+date[2], date[1] - 1, +date[0]);
    }
    return new Date(0);
  }

  function itemComparator(a, b, orderBy) {
    if (
      orderBy === 'lastAnomalyDate' ||
      orderBy === 'nextRevisionDate' ||
      orderBy === 'ticketCreationDate' ||
      orderBy === 'ticketClosingDate' ||
      orderBy === 'wsStartDate' ||
      orderBy === 'wsEndDate'
    ) {
      return descendingComparator(
        transformStringToDate(a[orderBy]),
        transformStringToDate(b[orderBy]),
      );
    }
    if (
      orderBy === 'wsDuration' ||
      orderBy === 'wsContractualDuration' ||
      orderBy === 'overdueTime' ||
      orderBy === 'creditNotes' ||
      orderBy === 'priority'
    ) {
      return descendingComparator(a[orderBy], b[orderBy]);
    }
    return descendingComparator(t(a[orderBy]), t(b[orderBy]));
  }

  async function handleClick(ticket) {
    const result = await ApiService.getTicketDetails(ticket.ticketNumber);
    if (user.internal || user.hasEmilyDRights || user.externalMax) {
      dispatch(setTicketNumber(result.data.ticket.ticketNumber));
      dispatch(setEditedTicket(result.data.ticket));
    } else {
      dispatch(setTicketNumber(result.data.ticketNumber));
      dispatch(setEditedTicket(result.data));
    }
    dispatch(setTicketStatus(ticket.ticketStatus));
    dispatch(setValidationErrorsMultiLang(result.data.validationErrors));
    history.push(`/ticket/details/${ticket.ticketNumber}`);
  }

  useEffect(() => {
    setDefaultSortConfig(
      selectedTicketTable === 'ticketHistoric'
        ? sortConfigTicketHistoric
        : selectedTicketTable === 'ticketList'
        ? sortConfig
        : sortConfigWorkshopTime,
    );
  }, [
    selectedTicketTable,
    sortConfig,
    sortConfigTicketHistoric,
    sortConfigWorkshopTime,
    currentViewName,
  ]);

  const sortedRows = useMemo(() => {
    const sorted = rows.slice();
    sorted.sort((a, b) => {
      // eslint-disable-next-line no-restricted-syntax
      for (const config of defaultSortConfig) {
        const result = itemComparator(a, b, config.propertyName);
        if (result !== 0) {
          if (config.sortType === 'desc') {
            return result;
          }
          return -result;
        }
      }
      return 0;
    });
    return sorted;
  }, [defaultSortConfig, rows]);

  return (
    <TableBody>
      {sortedRows
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        .map((row, index) => {
          const key = `row-key-${index}`;
          const labelId = `tickets-table-${index}`;
          return selectedTicketTable === 'ticketHistoric' ? (
            <TicketsHistoricRow
              key={key}
              labelId={labelId}
              row={row}
              handleClick={handleClick}
              classes={classes}
            />
          ) : selectedTicketTable === 'ticketList' ? (
            <TicketListRow
              headCells={headCells}
              key={key}
              labelId={labelId}
              row={row}
              handleClick={handleClick}
              classes={classes}
            />
          ) : (
            <WorkshopTimeRow
              key={key}
              labelId={labelId}
              row={row}
              handleClick={handleClick}
              classes={classes}
            />
          );
        })}
      {emptyRows > 0 && (
        <TableRow style={{ height: 43 * emptyRows }}>
          <TableCell colSpan={12} />
        </TableRow>
      )}
    </TableBody>
  );
}
